import React from 'react';

/**
 * Higher order component to simplify augmenting components with multiple sortable props with sorting info
 *
 * Pass your sortable component with the data to sort, then provide an object with keys that correspond to
 * keys in the sortable data and sort functions as values.  This will provide the following props to the wrapped
 * component: sortColumn, sortDirection, sortedData, onSort.  Use these to render current sort information and
 * provide onSort to sort data & update sort state info for free.
 *
 * If the default sorting behavior isn't what you need you can include a 4th parameter
 * to define the column the column and direction to sort on.
 *
 * @param WrappedComponent React.Component the component to decorate
 * @param unsortedData (props) => List : function to gather info to use as the sortable list
 * @param sorts: object - fields should correspond to fields in unsortedData, values provide sort functions
 *          - the first key in this object will become the default sort
 * @param defaultState: A json object to override the initial sorting state for column or direction
 * @returns object: the WrappedComponent decorated with sortColumn, sortDirection, sortedData, onSort
 */
export default function withSorting(WrappedComponent, unsortedData, sorts, defaultState = {}) {
  return class extends React.Component {
    constructor(props) {
      super(props);

      this.onSort = this.onSort.bind(this);

      this.state = {
        sortColumn: Object.keys(sorts)[0],
        sortDirection: 'desc',
        sortedData: null,
        ...defaultState,
      };
    }

    toggleSortDirection(sortDirection) {
      return sortDirection === 'asc' ? 'desc' : 'asc';
    }

    onSort(sortColumn, sortDirection) {
      const sortColumnChanged = sortColumn !== this.state.sortColumn;

      const newSortDirection = sortColumnChanged ? 'desc' : this.toggleSortDirection(sortDirection);

      this.setState({
        sortColumn: sortColumn,
        sortDirection: newSortDirection,
      });
    }

    applySorting(data, sortColumn, sortDirection) {
      const sort = sorts[sortColumn];

      // call the sort algo we received (passing the sort direction down),
      // then potentially invert that sort depending on our state sort order.
      return data.sort((i, j) => sort(i, j, sortDirection) * (sortDirection === 'asc' ? -1 : 1));
    }

    render() {
      const data = unsortedData(this.props);
      const sortedData = this.applySorting(data, this.state.sortColumn, this.state.sortDirection);

      return (
        <WrappedComponent
          onSort={this.onSort}
          sortedData={sortedData}
          sortColumn={this.state.sortColumn}
          sortDirection={this.state.sortDirection}
          {...this.props}
        />
      );
    }
  };
}
